import {
  Activity,
  DegressivePrice,
  DiscountCode,
  DiscountType,
  FileType,
  OrderStatus,
  Prisma,
  TicketUserType,
} from "@prisma/client";
import { fileSelectValues } from "./file.types";
import { onlineOrderDetailsInvoiceArgs } from "./invoice.types";
import { computablePricingValues, editablePricingValues, simplePricingValues } from "./pricing.types";
import { eventDateValues } from "./events-date.types";
import { unsafeUserValues, UserSharedProtected } from "./user.types";
import { unsafeVisitorValues, Visitor } from "./visitors.types";

const unsafeTicketValues = Prisma.validator<Prisma.TicketDefaultArgs>()({
  select: {
    user: unsafeUserValues,
    givenToVisitor: unsafeVisitorValues,
  },
});

const unsafeBadgeValues = Prisma.validator<Prisma.BadgeDefaultArgs>()({
  select: {
    user: unsafeUserValues,
    givenToVisitor: unsafeVisitorValues,
  },
});

export type UnsafeEntry =
  | Prisma.TicketGetPayload<typeof unsafeTicketValues>
  | Prisma.TicketGetPayload<typeof unsafeBadgeValues>;

export type UserSharedProtectedEntry<T extends UnsafeEntry> = Omit<T, "user" | "givenToVisitor"> & {
  user: UserSharedProtected<NonNullable<T["user"]>> | null;
  givenToVisitor: Visitor | null;
};

export const ticketingValues = Prisma.validator<Prisma.TicketingDefaultArgs>()({
  include: {
    eventDiscountCodes: {
      include: {
        discount: true,
        _count: {
          select: {
            orderItemDiscountCodes: {
              where: {
                orderItem: {
                  order: {
                    status: { in: [OrderStatus.pending, OrderStatus.success] },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
});

export type TicketingType = Prisma.TicketingGetPayload<typeof ticketingValues>;

// Fields the scanner app reads from its offline preload, kept for the app versions already in the stores
export const scannerPreloadTicketValues = Prisma.validator<Prisma.TicketDefaultArgs>()({
  select: {
    id: true,
    code: true,
    hmac: true,
    userId: true,
    ticketType: {
      select: {
        id: true,
        name: true,
        validityDates: true,
        numberEntriesPerDay: true,
        isInfiniteEntries: true,
        activity: { select: { name: true } },
        event: { select: { id: true, name: true } },
        pricing: { select: { price: { select: { priceTTC: true } } } },
        eventGates: { select: { id: true, name: true } },
      },
    },
    user: { select: { connectId: true, firstName: true, lastName: true, email: true } },
  },
});

export const ticketTypeItemValues = Prisma.validator<Prisma.TicketTypeDefaultArgs>()({
  include: {
    pricing: computablePricingValues,
    tickets: true,
  },
});

export type TicketTypeItemType = Prisma.TicketTypeGetPayload<typeof ticketTypeItemValues>;

export const ticketTypeValues = Prisma.validator<Prisma.TicketTypeDefaultArgs>()({
  include: {
    pricing: editablePricingValues,
    activity: { select: { eventId: true } },
    tickets: true,
    customAppearance: {
      include: {
        picture: {
          select: fileSelectValues,
        },
      },
    },
    customPdfAppearance: {
      include: {
        backgroundPicture: {
          select: fileSelectValues,
        },
        headerPicture: true,
        textPictures: true,
      },
    },
    templatePdf: true,
    eventGates: true,
    groupLimits: true,
  },
});

export const ticketTypeValuesWithCount = Prisma.validator<Prisma.TicketTypeDefaultArgs>()({
  include: {
    pricing: {
      select: {
        ...editablePricingValues.select,
        discountCodes: {
          ...editablePricingValues.select.discountCodes,
          select: {
            ...editablePricingValues.select.discountCodes?.select,
            discountCode: {
              select: {
                ...editablePricingValues.select.discountCodes?.select?.discountCode?.select,
                _count: {
                  select: {
                    orderItemDiscountCodes: {
                      where: {
                        orderItem: {
                          order: {
                            status: {
                              in: [OrderStatus.pending, OrderStatus.success],
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    activity: { select: { eventId: true } },
    groupLimits: true,
    _count: {
      select: {
        tickets: {
          where: {
            OR: [
              { orderItem: null },
              {
                orderItem: {
                  order: {
                    status: {
                      in: [OrderStatus.pending, OrderStatus.success],
                    },
                  },
                },
              },
            ],
          },
        },
      },
    },
    customAppearance: {
      include: {
        picture: true,
      },
    },
    customPdfAppearance: {
      include: {
        backgroundPicture: true,
        headerPicture: true,
        textPictures: true,
      },
    },
    templatePdf: true,
    eventGates: true,
  },
});

export type TicketTypeType = Prisma.TicketTypeGetPayload<typeof ticketTypeValues>;

export const ticketAndBadgesUserTypes = Object.values(TicketUserType);
export const ticketUserTypes = ticketAndBadgesUserTypes.filter(
  (t) => t !== "vendor" && t !== "business",
);
export const badgeUserTypes = ticketAndBadgesUserTypes.filter(
  (t) => t !== "vendor" && t !== "business",
);

export const getUserTypeLabel = (userType: TicketUserType) => {
  switch (userType) {
    case TicketUserType.all:
      return "Tous";
    case TicketUserType.visitor:
      return "Visiteur";
    case TicketUserType.exhibitor:
      return "Exposant";
    case TicketUserType.business:
      return "Entreprise";
    case TicketUserType.vendor:
      return "Vendeur";
    case TicketUserType.group:
      return "Groupe";
  }
};
export const discountTypes = Object.values(DiscountType);

export const getDiscountTypeLabel = (discountType: DiscountType) => {
  switch (discountType) {
    case DiscountType.euro:
      return "€";
    case DiscountType.percentage:
      return "%";
  }
};

export type DiscountCodeType = TicketingType["eventDiscountCodes"][number] & {
  // Live orders holding the code (one use = one order), added by the ticketing endpoint.
  usesCount: number;
};

export type TicketingWithUses = Omit<TicketingType, "eventDiscountCodes"> & {
  eventDiscountCodes: DiscountCodeType[];
};

const Ticket = Prisma.validator<Prisma.TicketFindManyArgs>()({
  include: {
    ticketType: true,
    givenToCommercial: {
      include: {
        user: {
          include: {
            picture: true,
          },
        },
      },
    },
  },
});

export type UserTicket = Prisma.TicketGetPayload<typeof Ticket>;

export type VisitorTicketType = UserTicket & {
  visitorData: Visitor;
};

export type ExhibitorTicker = UserTicket &
  TicketTypeType & {
    leftTickets: number;
    sentTickets: number;
  };

export interface AccessItemType {
  activity?: Activity;
  areDiscountsCodesEnabled: boolean;
  groupMinSize: number | null;
  groupOnly: boolean;
  isDegressivePrice: boolean;
  isPaid: boolean;
  isQuantityInfinit: boolean;
  isQuantityInfinite: boolean;
  maxValidityDate: string;
  minValidityDate: string;
  id: string;
  name: string;
  eventName: string;
  quantity: number;
  discountCodes: DiscountCode[];
  degressivePrices: DegressivePrice[];
  price: {
    id: string;
    priceTTC: string;
    vatRate: string;
  };
  userType: "all" | "organiser" | "attendee";
  type: "ticket" | "badge";
  quantityLeft: number | null;
}

export interface TicketForTicket {
  id: string;
  type: string;
  count: number;
  price: string;
  name: string;
  validityDates: string[];
}

export interface EventForTickets {
  id: string;
  name: string;
  type: string;
  description: string;
  startDate: string;
  endDate: string;
  address: {
    address: string;
    city: string;
    country: string;
    zipCode: string;
  };
  image: {
    key: string;
    type: FileType;
  };
}

export type ScanCount = {
  totalCount: number;
  countPerGate: Record<string, number>;
};

export const unsafeCompleteScannedTicketValues =
  Prisma.validator<Prisma.ScannedTicketDefaultArgs>()({
    include: {
      inspector: {
        include: {
          user: true,
        },
      },
      gate: true,
      ticket: {
        include: {
          ticketType: true,
          user: unsafeUserValues,
          givenToVisitor: unsafeVisitorValues,
        },
      },
      badge: {
        include: {
          badgeType: true,
          user: unsafeUserValues,
          givenToVisitor: unsafeVisitorValues,
        },
      },
    },
  });

export type UnsafeCompleteScannedTicket = Prisma.ScannedTicketGetPayload<
  typeof unsafeCompleteScannedTicketValues
>;

export type CompleteScannedTicket = Omit<UnsafeCompleteScannedTicket, "ticket" | "badge"> & {
  ticket: UserSharedProtectedEntry<NonNullable<UnsafeCompleteScannedTicket["ticket"]>> | null;
  badge: UserSharedProtectedEntry<NonNullable<UnsafeCompleteScannedTicket["badge"]>> | null;
};

export const userTicketValues = Prisma.validator<Prisma.TicketDefaultArgs>()({
  include: {
    orderItem: {
      include: {
        order: {
          include: {
            onlineDetails: {
              include: {
                invoice: onlineOrderDetailsInvoiceArgs,
              },
            },
          },
        },
      },
    },
    ticketType: {
      select: {
        id: true,
        name: true,
        validityDates: true,
        activity: true,
        event: {
          select: {
            id: true,
            address: true,
            image: true,
            name: true,
            dates: eventDateValues,
          },
        },
        pricing: simplePricingValues,
        numberEntriesPerDay: true,
        isInfiniteEntries: true,
        customAppearance: {
          include: {
            picture: true,
          },
        },
        customPdfAppearance: {
          include: {
            backgroundPicture: true,
            headerPicture: true,
            textPictures: true,
          },
        },
        eventGates: true,
      },
    },
    user: true,
  },
});

export type UserMyTicket = Prisma.TicketGetPayload<typeof userTicketValues>;

export const downloadTicketsSelect = Prisma.validator<Prisma.TicketSelect>()({
  id: true,
  code: true,
  hmac: true,
  ticketType: {
    select: {
      name: true,
      validityDates: true,
      pricingId: true,
      pricing: {
        select: {
          isPaid: true,
          price: {
            select: {
              id: true,
              priceTTC: true,
              vatRate: true,
            },
          },
        },
      },
      numberEntriesPerDay: true,
      isInfiniteEntries: true,
      customAppearance: {
        include: {
          picture: true,
        },
      },
      customPdfAppearance: {
        include: {
          backgroundPicture: true,
          headerPicture: true,
          textPictures: true,
        },
      },
      templatePdf: true,
      eventGates: {
        select: {
          id: true,
        },
      },
    },
  },
  user: {
    select: {
      firstName: true,
      lastName: true,
    },
  },
});

export type TicketWithDownloadInfo = Prisma.TicketGetPayload<{
  select: typeof downloadTicketsSelect;
}>;

export const scannedTicketValues = Prisma.validator<Prisma.TicketDefaultArgs>()({
  include: {
    ticketType: {
      include: {
        event: {
          include: {
            gates: true,
          },
        },
        eventGates: true,
        activity: {
          select: {
            id: true,
            structureId: true,
          },
        },
      },
    },
  },
});

export type ScannedTicket = Prisma.TicketGetPayload<typeof scannedTicketValues>;

export const ticketTypeForAlertThresholdValues = Prisma.validator<Prisma.TicketTypeDefaultArgs>()({
  select: {
    id: true,
    name: true,
    quantityLeft: true,
    isQuantityInfinite: true,
    hasAlertThreshold: true,
    alertThreshold: true,
  },
});

export type TicketTypeForAlertThreshold = Prisma.TicketTypeGetPayload<
  typeof ticketTypeForAlertThresholdValues
>;
